import React, { forwardRef, useContext, useState } from 'react';
import { ReactEditor, useSlateStatic } from 'slate-react';
import { Transforms } from 'slate';
import classnames from 'classnames';

import Show from 'components/Show';
import { Checkbox, Button } from 'components/Misc/Form';

import { EditorContext } from 'components/Editor';

const Option = forwardRef(({ children, element, ...attributes }, ref) => {
	// Get editor's instance
	const editor = useSlateStatic();

	// Use editor's context
	const { quiz } = useContext(EditorContext);

	// Store options value
	const [answer, setAnswer] = useState(false);

	// Get readonly state
	const readOnly = ReactEditor.isReadOnly(editor);

	// Concat container classnames
	const containerClassName = classnames(
		'mt-2 flex w-full space-x-2',
		readOnly && 'items-center',
		(quiz.mode === 'review' && answer && element.correct !== answer) && 'border-l-4 pl-2 border-rose-500',
		(quiz.mode === 'review' && element.correct && answer) && 'border-l-4 pl-2 border-emerald-500',
	);

	// Concat option classnames
	const optionClassName = classnames(
		'flex-1 text-base leading-7 text-neutral-800 dark:text-white transition-colors',
		!readOnly && 'py-2 px-3 border border-neutral-200 dark:border-neutral-700 rounded-lg'
	);

	// Handle correct change
	const onChange = ({ value }) => {
		// Update option in document
		if (quiz.mode === 'edit') {
			const path = ReactEditor.findPath(editor, element);
			Transforms.setNodes(editor, { correct: value }, { at: path });
		}

		// Update option in state
		if (quiz.mode === 'answer') {
			setAnswer(value);
		}
	};

	// Handle option removal
	const onRemove = () => {
		const path = ReactEditor.findPath(editor, element);
		editor.deleteQuizOption(path);
	};

	return (
		<div className={containerClassName}>
			{/* Option's controls */}
			<div contentEditable={false} className="flex flex-col space-y-2">
				<Checkbox
					small
					name={element.key}
					readOnly={['view', 'review'].includes(quiz.mode)}
					value={['view', 'answer', 'review'].includes(quiz.mode) ? answer : element.correct}
					onChange={onChange}
				/>

				{/* Show only if editable */}
				<Show if={quiz.mode === 'edit'}>
					<Button
						small
						square
						icon="delete"
						theme="danger"
						onClick={onRemove}
						className="font-normal"
					/>
				</Show>
			</div>

			{/* Option's content */}
			<div
				ref={ref}
				{...attributes}
				className={optionClassName}
			>
				{children}
			</div>
		</div>
	);
});

export default Option;
